import type { UseFormSetValue } from "react-hook-form";
import { DateTime } from "luxon";
import type {
  MembershipAchievementCategoryEnum,
  MembershipAchievementForMember,
  MembershipAchievementType
} from "@/pages/people/[pgaId]/queries";
import type {
  UpsertAchievementAttributeMutationVariables,
  UpsertAchievementCodeMutationVariables,
  UpsertNativeAchievementMutationVariables
} from "@/pages/people/[pgaId]/mutations";
import type {
  AchievementFormCapabilities,
  AchievementKind,
  AchievementKindStrategy,
  AchievementTypeOption,
  EditAchievementFormValues,
  FormNameForCapability,
  SaveAchievementMutations,
  SectionOptionSource
} from "./types";

export const DEFAULT_CAPABILITIES: AchievementFormCapabilities = {
  title: false,
  recognizedAt: true,
  recognitionPeriod: true,
  expiresOn: false,
  recognizedBy: false,
  remarks: false
};

export function getRecognizedByFormName(
  sectionValueKey: "id" | "legacyId" | null
): FormNameForCapability<"recognizedBy"> {
  return sectionValueKey === "id" ? "managingSectionId" : "sectionLegacyId";
}

function validateAchievementType(values: EditAchievementFormValues, categoryLabel: string) {
  if (values.native) {
    if (!values.achievementTypeId.trim()) return `${categoryLabel} type is required.`;
  } else if (!values.code.trim() || (!values.ccmsCodeType.trim() && !values.ccmsAttributeType.trim())) {
    return `${categoryLabel} type is required.`;
  }

  return null;
}

function validateRecognizedAt(values: EditAchievementFormValues) {
  if (!values.recognizedAt) return "Recognized At is required.";
  return null;
}

const nativeStrategy: AchievementKindStrategy = {
  capabilities: {
    title: true,
    recognizedAt: true,
    recognitionPeriod: true,
    expiresOn: true,
    recognizedBy: true,
    remarks: true
  },
  sectionValueKey: "id",
  lockRecognizedAtOnEdit: false,
  validate(values, categoryLabel) {
    const typeError = validateAchievementType(values, categoryLabel);
    if (typeError) return typeError;

    const recognizedAtError = validateRecognizedAt(values);
    if (recognizedAtError) return recognizedAtError;

    if (values.expiresOn && values.expiresOn < values.recognizedAt) {
      return "Expires On cannot be before Recognized At.";
    }

    return null;
  },
  async save({ achievementId, includeSelfReported, mutations, pgaId, values }) {
    const { data } = await mutations.upsertNativeAchievement({
      variables: {
        input: buildUpsertNativeAchievementInput(pgaId, values, achievementId, includeSelfReported)
      }
    });

    return {
      success: Boolean(data?.upsertNativeAchievement.success),
      message: data?.upsertNativeAchievement.message
    };
  }
};

const codeStrategy: AchievementKindStrategy = {
  capabilities: {
    title: false,
    recognizedAt: true,
    recognitionPeriod: true,
    expiresOn: false,
    recognizedBy: true,
    remarks: true
  },
  sectionValueKey: "legacyId",
  lockRecognizedAtOnEdit: true,
  validate(values, categoryLabel) {
    const typeError = validateAchievementType(values, categoryLabel);
    if (typeError) return typeError;

    return validateRecognizedAt(values);
  },
  async save({ mutations, pgaId, values }) {
    const { data } = await mutations.upsertAchievementCode({
      variables: {
        input: buildUpsertAchievementCodeInput(pgaId, values)
      }
    });

    return {
      success: Boolean(data?.upsertAchievementCode.success),
      message: data?.upsertAchievementCode.message
    };
  }
};

const attributeStrategy: AchievementKindStrategy = {
  capabilities: {
    title: true,
    recognizedAt: true,
    recognitionPeriod: true,
    expiresOn: false,
    recognizedBy: false,
    remarks: false
  },
  sectionValueKey: null,
  lockRecognizedAtOnEdit: false,
  validate(values, categoryLabel) {
    const typeError = validateAchievementType(values, categoryLabel);
    if (typeError) return typeError;

    return validateRecognizedAt(values);
  },
  async save({ mutations, pgaId, values }) {
    const { data } = await mutations.upsertAchievementAttribute({
      variables: {
        input: buildUpsertAchievementAttributeInput(pgaId, values)
      }
    });

    return {
      success: Boolean(data?.upsertAchievementAttribute.success),
      message: data?.upsertAchievementAttribute.message
    };
  }
};

export const ACHIEVEMENT_KIND_STRATEGIES: Record<AchievementKind, AchievementKindStrategy> = {
  native: nativeStrategy,
  code: codeStrategy,
  attribute: attributeStrategy
};

function getAchievementKind(
  isNative: boolean,
  ccmsCodeType: string,
  ccmsAttributeType: string
): AchievementKind | null {
  if (isNative) return "native";
  if (ccmsCodeType) return "code";
  if (ccmsAttributeType) return "attribute";
  return null;
}

export function getAchievementStrategy(
  isNative: boolean,
  ccmsCodeType: string,
  ccmsAttributeType: string
): AchievementKindStrategy | null {
  const kind = getAchievementKind(isNative, ccmsCodeType, ccmsAttributeType);
  return kind ? ACHIEVEMENT_KIND_STRATEGIES[kind] : null;
}

export const emptyFormValues: EditAchievementFormValues = {
  achievementTypeId: "",
  ccmsAttributeType: "",
  ccmsCodeType: "",
  code: "",
  expiresOn: "",
  managingSectionId: "",
  native: false,
  recognizedAt: "",
  recognitionPeriod: "",
  remarks: "",
  sectionLegacyId: "",
  selfReported: false,
  title: ""
};

export function getCategoryLabel(category: MembershipAchievementCategoryEnum) {
  if (category === "AWARD") return "Award";
  if (category === "CERTIFICATION") return "Certification";
  return "Achievement";
}

export function defaultExpiresOnFromRecognition(recognizedAt: string, defaultExpirationDays?: number | null): string {
  if (!recognizedAt || !defaultExpirationDays) return "";
  return DateTime.fromISO(recognizedAt).plus({ days: defaultExpirationDays }).toISODate() ?? "";
}

export function buildAchievementTypeOptions(
  achievementTypes: MembershipAchievementType[] = []
): AchievementTypeOption[] {
  return achievementTypes.flatMap<AchievementTypeOption>((achievementType) => {
    if (achievementType.native) {
      return [
        {
          achievementTypeId: achievementType.id,
          ccmsAttributeType: null,
          ccmsCodeType: null,
          label: achievementType.title,
          native: true,
          value: achievementType.id
        }
      ];
    }

    const ccmsCodeType = achievementType.ccmsCodeType?.trim();
    if (ccmsCodeType) {
      return achievementType.codes.flatMap((code) => {
        const value = code.code?.trim();
        if (!value) return [];

        return [
          {
            achievementTypeId: achievementType.id,
            ccmsAttributeType: null,
            ccmsCodeType,
            label: code.title || achievementType.title,
            native: false,
            value
          }
        ];
      });
    }

    const ccmsAttributeType = achievementType.ccmsAttributeType?.trim();
    const attributeCode = achievementType.codes.find((code) => code.code?.trim())?.code?.trim();
    if (!ccmsAttributeType || !attributeCode) return [];

    return [
      {
        achievementTypeId: achievementType.id,
        ccmsAttributeType,
        ccmsCodeType: null,
        label: achievementType.title,
        native: false,
        value: attributeCode
      }
    ];
  });
}

function findSectionLegacyIdByName(sections: SectionOptionSource[] = [], sectionName: string | null | undefined) {
  if (!sectionName?.trim()) return "";

  const normalizedName = sectionName.trim().toLowerCase();

  return sections.find((section) => section.name.trim().toLowerCase() === normalizedName)?.legacyId ?? "";
}

export function resolveSectionLegacyId(
  sections: SectionOptionSource[] = [],
  recognizingEntity: { legacyId?: string | null; name?: string | null } | null | undefined
) {
  if (!recognizingEntity) return "";
  if (recognizingEntity.legacyId) return recognizingEntity.legacyId;
  return findSectionLegacyIdByName(sections, recognizingEntity.name);
}

export function sectionsForOptions(
  manageableSections: SectionOptionSource[] = [],
  currentEntity?: { id?: string | null; legacyId?: string | null; name?: string | null } | null
) {
  if (!currentEntity?.name) return manageableSections;

  const hasId = Boolean(currentEntity.id);
  const hasLegacyId = Boolean(currentEntity.legacyId);
  if (!hasId && !hasLegacyId) return manageableSections;

  const alreadyIncluded = manageableSections.some(
    (section) =>
      (hasId && section.id === currentEntity.id) || (hasLegacyId && section.legacyId === currentEntity.legacyId)
  );
  if (alreadyIncluded) return manageableSections;

  return [...manageableSections, { id: currentEntity.id, legacyId: currentEntity.legacyId, name: currentEntity.name }];
}

export function buildSectionOptions(sections: SectionOptionSource[] = [], valueKey: "id" | "legacyId") {
  return [...sections]
    .filter((section) => section[valueKey])
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((section) => ({
      label: section.name,
      value: section[valueKey]!
    }));
}

export function buildUpsertAchievementCodeInput(
  pgaId: string,
  values: EditAchievementFormValues
): UpsertAchievementCodeMutationVariables["input"] {
  const trimmedYear = values.recognitionPeriod.trim();
  const trimmedRemarks = values.remarks.trim();

  return {
    pgaId,
    code: values.code,
    codeType: values.ccmsCodeType,
    asofDate: values.recognizedAt,
    awardYear: trimmedYear || null,
    awardRemarks: trimmedRemarks || null,
    referenceId: values.sectionLegacyId || null
  };
}

export function buildUpsertAchievementAttributeInput(
  pgaId: string,
  values: EditAchievementFormValues
): UpsertAchievementAttributeMutationVariables["input"] {
  const trimmedTitle = values.title.trim();
  const trimmedYear = values.recognitionPeriod.trim();
  const parsedYear = trimmedYear ? Number(trimmedYear) : null;

  return {
    pgaId,
    attributeCode: values.code,
    attributeType: values.ccmsAttributeType,
    cvar: trimmedTitle || null,
    dvar: values.recognizedAt || null,
    nvar: parsedYear
  };
}

export function buildUpsertNativeAchievementInput(
  pgaId: string,
  values: EditAchievementFormValues,
  achievementId?: string | null,
  includeSelfReported = !achievementId
): UpsertNativeAchievementMutationVariables["input"] {
  const trimmedTitle = values.title.trim();
  const trimmedYear = values.recognitionPeriod.trim();
  const trimmedRemarks = values.remarks.trim();
  const parsedYear = trimmedYear ? Number(trimmedYear) : null;

  const input: UpsertNativeAchievementMutationVariables["input"] = {
    pgaId,
    achievementTypeId: values.achievementTypeId,
    id: achievementId || null,
    title: trimmedTitle || null,
    recognizedAt: values.recognizedAt,
    recognitionYear: parsedYear,
    expiresOn: values.expiresOn || null,
    remarks: trimmedRemarks || null,
    managingSectionId: values.managingSectionId || null
  };

  if (includeSelfReported) {
    input.selfReported = values.selfReported;
  }

  return input;
}

export function buildEditFormValues({
  achievement,
  matchingAchievementType,
  sections
}: {
  achievement: MembershipAchievementForMember;
  matchingAchievementType: MembershipAchievementType | undefined;
  sections: SectionOptionSource[] | undefined;
}): { typeLabel: string; values: EditAchievementFormValues } {
  const isNativeType = Boolean(matchingAchievementType?.native);
  const codeType = matchingAchievementType?.ccmsCodeType?.trim() ?? "";
  const attributeType = matchingAchievementType?.ccmsAttributeType?.trim() ?? "";
  const code = isNativeType
    ? (matchingAchievementType?.id ?? achievement.typeId)
    : (achievement.code.code?.trim() ?? "");
  const typeLabel = isNativeType
    ? matchingAchievementType?.title || achievement.title || ""
    : codeType
      ? achievement.code.title || achievement.title || matchingAchievementType?.title || ""
      : matchingAchievementType?.title || achievement.title || "";

  return {
    typeLabel,
    values: {
      code,
      achievementTypeId: matchingAchievementType?.id ?? achievement.typeId,
      native: isNativeType,
      ccmsCodeType: isNativeType ? "" : codeType,
      ccmsAttributeType: isNativeType || codeType ? "" : attributeType,
      title: achievement.title ?? (codeType ? achievement.code.title : ""),
      recognizedAt: achievement.recognizedAt || achievement.createdDate || "",
      recognitionPeriod: achievement.recognitionYear ?? "",
      expiresOn: achievement.expiresOn ?? "",
      remarks: achievement.remarks ?? "",
      selfReported: false,
      sectionLegacyId: isNativeType ? "" : resolveSectionLegacyId(sections, achievement.recognizingEntity),
      managingSectionId: isNativeType ? (achievement.recognizingEntity?.id ?? "") : ""
    }
  };
}

export function validateEditAchievementForm(values: EditAchievementFormValues, categoryLabel: string) {
  const strategy = getAchievementStrategy(values.native, values.ccmsCodeType, values.ccmsAttributeType);

  if (strategy) {
    return strategy.validate(values, categoryLabel);
  }

  const typeError = validateAchievementType(values, categoryLabel);
  if (typeError) return typeError;

  return validateRecognizedAt(values);
}

export async function saveAchievement({
  achievementId,
  includeSelfReported,
  mutations,
  pgaId,
  values,
  categoryLabel
}: {
  achievementId?: string | null;
  includeSelfReported?: boolean;
  mutations: SaveAchievementMutations;
  pgaId: string;
  values: EditAchievementFormValues;
  categoryLabel: string;
}): Promise<{ message?: string | null; success: boolean }> {
  const validationError = validateEditAchievementForm(values, categoryLabel);
  if (validationError) {
    return { success: false, message: validationError };
  }

  const strategy = getAchievementStrategy(values.native, values.ccmsCodeType, values.ccmsAttributeType);
  if (!strategy) {
    return { success: false, message: `${categoryLabel} type is required.` };
  }

  return strategy.save({ pgaId, achievementId, values, mutations, includeSelfReported });
}

export function clearAchievementTypeSelection(
  onChange: (value: string) => void,
  setValue: UseFormSetValue<EditAchievementFormValues>
) {
  onChange("");
  setValue("achievementTypeId", "", { shouldDirty: true });
  setValue("native", false, { shouldDirty: true });
  setValue("ccmsCodeType", "", { shouldDirty: true });
  setValue("ccmsAttributeType", "", { shouldDirty: true });
  setValue("managingSectionId", "", { shouldDirty: true });
  setValue("sectionLegacyId", "", { shouldDirty: true });
  setValue("expiresOn", "", { shouldDirty: true });
  setValue("selfReported", false, { shouldDirty: true });
}

export function applyAchievementTypeSelection({
  currentTitle,
  onChange,
  option,
  options,
  previousCode,
  setValue
}: {
  currentTitle: string;
  onChange: (value: string) => void;
  option: AchievementTypeOption;
  options: AchievementTypeOption[];
  previousCode: string;
  setValue: UseFormSetValue<EditAchievementFormValues>;
}) {
  const previousOption = options.find((achievementTypeOption) => achievementTypeOption.value === previousCode);

  onChange(option.value);
  setValue("achievementTypeId", option.achievementTypeId, { shouldDirty: true });
  setValue("native", option.native, { shouldDirty: true });
  setValue("ccmsCodeType", option.ccmsCodeType ?? "", { shouldDirty: true });
  setValue("ccmsAttributeType", option.ccmsAttributeType ?? "", { shouldDirty: true });
  setValue("managingSectionId", "", { shouldDirty: true });
  setValue("sectionLegacyId", "", { shouldDirty: true });
  setValue("expiresOn", "", { shouldDirty: true });
  setValue("selfReported", false, { shouldDirty: true });

  if (!currentTitle.trim() || currentTitle === previousOption?.label) {
    setValue("title", option.label, { shouldDirty: true, shouldValidate: true });
  }
}
